import type { MetricCardVisualization } from '../../types';
import { SparklineChart } from './SparklineChart';

interface MetricCardRendererProps {
    /** MetricCard visualization data */
    data: MetricCardVisualization;
    /** Optional className for custom styling */
    className?: string;
}

/**
 * Renders a MetricCard (KPI card) visualization.
 * Displays a title, primary value, optional trend indicator, supporting text, and sparkline.
 */
export function MetricCardRenderer({ data, className }: MetricCardRendererProps) {
    const { title, primaryValue, supportingText, trend, sparkline } = data;

    // Determine trend color based on sentiment
    const getTrendColor = () => {
        if (!trend) return undefined;
        switch (trend.sentiment) {
            case 'positive':
                return 'var(--weavable-trend-positive, #2E7D32)';
            case 'negative':
                return 'var(--weavable-trend-negative, #C62828)';
            case 'neutral':
                return 'var(--weavable-trend-neutral, #6b7280)';
        }
    };

    // Get trend arrow based on direction
    const getTrendArrow = () => {
        if (!trend) return '';
        switch (trend.direction) {
            case 'up':
                return '\u25B2'; // ▲
            case 'down':
                return '\u25BC'; // ▼
            case 'neutral':
                return '–';
        }
    };

    return (
        <div className={`weavable-metric-card ${className || ''}`.trim()}>
            <div className="weavable-metric-card__header">
                <h3 className="weavable-metric-card__title">{title}</h3>
                <span
                    className="weavable-metric-card__trend-badge"
                    style={trend ? { color: getTrendColor() } : { visibility: 'hidden' }}
                >
                    <span className="weavable-metric-card__trend-arrow">{trend ? getTrendArrow() : '\u25B2'}</span>
                    <span className="weavable-metric-card__trend-value">{trend?.displayValue || '0%'}</span>
                    {trend?.comparisonLabel && (
                        <span className="weavable-metric-card__comparison-label">{trend.comparisonLabel}</span>
                    )}
                </span>
            </div>

            <div className="weavable-metric-card__body">
                <span className="weavable-metric-card__primary-value">{primaryValue}</span>

                {supportingText && <p className="weavable-metric-card__supporting-text">{supportingText}</p>}

                {sparkline && (
                    <div className="weavable-metric-card__sparkline">
                        {/* sparkline.color is intentionally not passed — color is controlled via the
                           analyticsSparklineColor theme prop (CSS var --weavable-sparkline-color) */}
                        <SparklineChart
                            data={sparkline.data}
                            labels={sparkline.labels}
                            chartType={sparkline.chartType}
                        />
                        {(sparkline.startLabel || sparkline.endLabel) && (
                            <div className="weavable-metric-card__sparkline-labels">
                                <span>{sparkline.startLabel}</span>
                                <span>{sparkline.endLabel}</span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
